import BaseVehicle from '@FreeRider/Player/Vehicles/BaseVehicle';
import Track from '@FreeRider/Track';
import Mass from '@FreeRider/Physics/Mass';
import Spring from '@FreeRider/Physics/Spring';
import Player from '@FreeRider/Player/Player';

/**
 * Stickman Type
 * This is used to build the stickman object from a vehicle
 */
export type Stickman = {
    head_x: number;
    head_y: number;
    left_hand_x: number;
    left_hand_y: number;
    right_hand_x: number;
    right_hand_y: number;
    left_elbow_x: number;
    left_elbow_y: number;
    right_elbow_x: number;
    right_elbow_y: number;
    waist_x: number;
    waist_y: number;
    left_foot_x: number;
    left_foot_y: number;
    right_foot_x: number;
    right_foot_y: number;
    left_knee_x: number;
    left_knee_y: number;
    right_knee_x: number;
    right_knee_y: number;
    head_vel_x: number;
    head_vel_y: number;
    feet_vel_x: number;
    feet_vel_y: number;
    dir: number;
}


/**
 * Mass Positions
 */
export enum RagdollMassesMap
{
    HEAD = 0,
    WAIST = 1,
    LEFT_ELBOW = 2,
    RIGHT_ELBOW = 3,
    LEFT_HAND = 4,
    RIGHT_HAND = 5,
    LEFT_KNEE = 6,
    RIGHT_KNEE = 7,
    LEFT_FOOT = 8,
    RIGHT_FOOT = 9
}

class Ragdoll extends BaseVehicle
{
    public masses: Mass[];
    public springs: Spring[];
    public dir: number;

    constructor(player: Player)
    {
        super(player)
        this.createMasses();
        this.createSprings();
    }

    private createMasses()
    {
        const head = new Mass(this);
        const waist = new Mass(this);
        const left_elbow = new Mass(this);
        const right_elbow = new Mass(this);
        const left_hand = new Mass(this);
        const right_hand = new Mass(this);
        const left_knee = new Mass(this);
        const right_knee = new Mass(this);
        const left_foot = new Mass(this);
        const right_foot = new Mass(this);

        const masses = [];
        masses.push(head);
        masses.push(waist);
        masses.push(left_elbow);
        masses.push(right_elbow);
        masses.push(left_hand);
        masses.push(right_hand);
        masses.push(left_knee);
        masses.push(right_knee);
        masses.push(left_foot);
        masses.push(right_foot);

        for (let i in masses)
        {
            masses[i].radius = 3;
            masses[i].friction = 0.05;
        }

        head.radius = waist.radius = 8;

        this.masses = masses;
    }

    private createSprings()
    {
        const masses = this.masses;

        const head = masses[RagdollMassesMap.HEAD];
        const waist = masses[RagdollMassesMap.WAIST];
        const left_elbow = masses[RagdollMassesMap.LEFT_ELBOW];
        const right_elbow = masses[RagdollMassesMap.RIGHT_ELBOW];
        const left_hand = masses[RagdollMassesMap.LEFT_HAND];
        const right_hand = masses[RagdollMassesMap.RIGHT_HAND];
        const left_knee = masses[RagdollMassesMap.LEFT_KNEE];
        const right_knee = masses[RagdollMassesMap.RIGHT_KNEE];
        const left_foot = masses[RagdollMassesMap.LEFT_FOOT];
        const right_foot = masses[RagdollMassesMap.RIGHT_FOOT];

        const springs = [];
        springs.push(new Spring(head, waist, this));
        springs.push(new Spring(head, left_elbow, this));
        springs.push(new Spring(left_elbow, left_hand, this));
        springs.push(new Spring(head, right_elbow, this));
        springs.push(new Spring(right_elbow, right_hand, this));
        springs.push(new Spring(waist, left_knee, this));
        springs.push(new Spring(left_knee, left_foot, this));
        springs.push(new Spring(waist, right_knee, this));
        springs.push(new Spring(right_knee, right_foot, this));

        for (let i in springs)
        {
            springs[i].spring_constant = 0.4;
            springs[i].damp_constant = 0.7;
        }


        this.springs = springs;
    }

    public reset(stickman: Stickman)
    {

        const masses = this.masses;

        const head = masses[RagdollMassesMap.HEAD];
        const waist = masses[RagdollMassesMap.WAIST];
        const left_elbow = masses[RagdollMassesMap.LEFT_ELBOW];
        const right_elbow = masses[RagdollMassesMap.RIGHT_ELBOW];
        const left_hand = masses[RagdollMassesMap.LEFT_HAND];
        const right_hand = masses[RagdollMassesMap.RIGHT_HAND];
        const left_knee = masses[RagdollMassesMap.LEFT_KNEE];
        const right_knee = masses[RagdollMassesMap.RIGHT_KNEE];
        const left_foot = masses[RagdollMassesMap.LEFT_FOOT];
        const right_foot = masses[RagdollMassesMap.RIGHT_FOOT];


        head.position.x = stickman.head_x;
        head.position.y = stickman.head_y;

        waist.position.x = stickman.waist_x;
        waist.position.y = stickman.waist_y;

        left_elbow.position.x = stickman.left_elbow_x;
        left_elbow.position.y = stickman.left_elbow_y;

        right_elbow.position.x = stickman.right_elbow_x;
        right_elbow.position.y = stickman.right_elbow_y;

        right_hand.position.x = stickman.right_hand_x;
        right_hand.position.y = stickman.right_hand_y;

        left_hand.position.x = stickman.left_hand_x;
        left_hand.position.y = stickman.left_hand_y;

        left_knee.position.x = stickman.left_knee_x;
        left_knee.position.y = stickman.left_knee_y;

        right_knee.position.x = stickman.right_knee_x;
        right_knee.position.y = stickman.right_knee_y;

        left_foot.position.x = stickman.left_foot_x;
        left_foot.position.y = stickman.left_foot_y;

        right_foot.position.x = stickman.right_foot_x;
        right_foot.position.y = stickman.right_foot_y;

        const p1vel_x = stickman.head_vel_x * 0.7;
        const p1vel_y = stickman.head_vel_y * 0.7;

        const p2vel_x = stickman.feet_vel_x * 0.7;
        const p2vel_y = stickman.feet_vel_y * 0.7;


        const springs = this.springs;

        for (let i in springs)
        {
            const spring = springs[i];

            const m1_pos = spring.m1.position;
            const m2_pos = spring.m2.position;

            const len = Math.sqrt(Math.pow(m2_pos.x - m1_pos.x, 2) + Math.pow(m2_pos.y - m1_pos.y, 2));

            spring.lrest = len;
            spring.leff = len;
        }

        for (let i = 1; i <= 4; i++)
        {
            const spring = springs[i];
            spring.lrest = 13;
            spring.leff = 13;
        }

        for (let i in springs)
        {
            const spring = springs[i];
            if (spring.leff > 20)
            {
                spring.lrest = 20;
                spring.leff = 20;
            }
        }

        var upper_body = [
            head,
            left_elbow,
            right_elbow,
            left_hand,
            right_hand
        ];

        var lower_body = [
            waist,
            left_knee,
            right_knee,
            left_foot,
            right_foot
        ];

        for (let i in upper_body)
        {
            var upper_body_part = upper_body[i];
            var upper_body_part_pos = upper_body_part.position;
            upper_body_part.old.x = upper_body_part_pos.x - p1vel_x;
            upper_body_part.old.y = upper_body_part_pos.y - p1vel_y;
        }

        for (let i in lower_body)
        {
            var lower_body_part = lower_body[i];
            var lower_body_part_pos = lower_body_part.position;
            lower_body_part.old.x = lower_body_part_pos.x - p2vel_x;
            lower_body_part.old.y = lower_body_part_pos.y - p2vel_y;
        }

        for (let i in masses)
        {
            var mass = masses[i];
            var mass_vel_x = mass.position.x - mass.old.x;
            var mass_vel_y = mass.position.y - mass.old.y;

            mass.vel.x = mass_vel_x;
            mass.vel.y = mass_vel_y;

            mass.vel.x += (Math.random() - Math.random()) * 1;
            mass.vel.y += (Math.random() - Math.random()) * 1;
        }

        this.dir = stickman.dir;
    }

    update(track: Track)
    {
        var springs = this.springs;
        var masses = this.masses;
        var spring_count = springs.length;
        var mass_count = masses.length;

        for (var s = spring_count - 1; s >= 0; s--)
        {
            springs[s].update();
        }

        for (var m = mass_count - 1; m >= 0; m--)
        {
            masses[m].update(track);
        }
    }

    getFocalPoint(): Mass
    {
        return this.masses[RagdollMassesMap.HEAD];
    }
}

export default Ragdoll;